import { Flex, InfoBox } from "@eworkit/iris";
import React, { FunctionComponent, memo, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IFetcherRenderProps } from "@/components/Fetcher";
import { If } from "@/components/If";
import { UserContext } from "@/components/User";
import { isProfileConnected } from "@/data/consultant-profiles";
import { fetchProfileDraftApplications, IJobApplication } from "@/data/job-applications";
import { IJobRequest } from "@/data/job-requests";
import { useJobRequestApplyAvailability } from "@/data/job-requests/hooks/useJobRequestApplyAvailability";
import { usePreviousRoutePath } from "@/infrastructure/Route";
import { BackButton } from "@/views/components/BackButton";
import { JobRequestIconHeader } from "@/views/components/JobRequests/Partials";
import { JobRequestApplyButton } from "@/views/components/JobRequests/Partials/JobRequestIconHeader/JobRequestApplyButton";
import { PageHeader } from "@/views/components/PageHeader";
import { router } from "@/views/router";

export interface IJobRequestHeaderProps extends IFetcherRenderProps<IJobRequest> {
  onApplyModalClose?: () => void;
}

export const JobRequestHeader: FunctionComponent<IJobRequestHeaderProps> = memo(({ payload, onApplyModalClose }) => {
  const { t } = useTranslation(["public", "session"]);
  const { contextConsultantProfile, contextConsultantProfileId, contextCompany } = useContext(UserContext);

  const { previousPath } = usePreviousRoutePath();

  const [drafts, setDrafts] = useState<IJobApplication[]>();

  const refreshDrafts = useCallback(async () => {
    if (
      !payload ||
      !contextConsultantProfile ||
      !contextConsultantProfileId ||
      isProfileConnected(contextConsultantProfile)
    ) {
      return;
    }

    try {
      const drafts = await fetchProfileDraftApplications({
        jobRequestId: payload.id,
        profileId: contextConsultantProfileId,
      });
      setDrafts(drafts);
    } catch (error) {
      // NOTE: we ignore errors if drafts cannot be fetched
    }
  }, [contextConsultantProfile, contextConsultantProfileId, payload]);

  const handleApplyModalClosed = useCallback(async () => {
    await refreshDrafts();
    onApplyModalClose?.();
  }, [refreshDrafts, onApplyModalClose]);

  useEffect(() => {
    refreshDrafts();
  }, [refreshDrafts]);

  const { isExpired, isMember, isVatInvalid, isForCompanies, isApplyAvailable, isVatRequired, isOrgInvalid } =
    useJobRequestApplyAvailability(payload);

  if (!payload) {
    return null;
  }

  const alreadyApplied =
    contextConsultantProfile && !isProfileConnected(contextConsultantProfile) && payload.alreadyApplied;
  const anyDraft = Boolean(drafts?.length);

  return (
    <PageHeader key={payload.id}>
      <Flex flexDirection="column" flexGrow={1}>
        <Flex>
          <BackButton
            path={
              !previousPath || previousPath?.includes(router.get("public-job-requests").path + "/")
                ? router.get("public-job-requests").path
                : previousPath
            }
            label={t("session:JOB_REQUESTS.DETAIL.BACK_TO_JOBS")}
            variant={"tab"}
          />
        </Flex>

        <Flex flexDirection="column" gap="sm">
          {isExpired ? (
            <InfoBox variant="warning" text={t("session:JOB_REQUESTS.DETAIL.IS_EXPIRED")} />
          ) : contextCompany && (isVatInvalid || isVatRequired || isOrgInvalid) ? (
            <InfoBox variant="warning" text={t("session:JOB_REQUESTS.DETAIL.INVALID_VAT_NUMBER")} />
          ) : isMember ? (
            <InfoBox variant="warning" text={t("session:JOB_REQUESTS.DETAIL.CONTACT_MANAGER_INFO")} />
          ) : isForCompanies ? (
            <InfoBox variant="warning" text={t("session:JOB_REQUESTS.DETAIL.JR_FOR_BUSINESS_WARNING")} />
          ) : alreadyApplied && !anyDraft ? (
            <InfoBox variant="information" text={t("session:JOB_REQUESTS.DETAIL.ALREADY_APPLIED_WARNING")} />
          ) : anyDraft ? (
            <InfoBox variant="information" text={t("session:JOB_REQUESTS.DETAIL.DRAFT_WARNING")} />
          ) : null}

          <If predicate={!contextCompany && !isVatRequired}>
            <InfoBox variant="warning" text={t("session:JOB_REQUESTS.DETAIL.PRIVATE_PERSON_WARNING")} />
          </If>

          <Flex p="xxs" gap="sm">
            <JobRequestIconHeader record={payload} />
            {isApplyAvailable && (
              <JobRequestApplyButton jobRequest={payload} drafts={drafts} onApplyModalClosed={handleApplyModalClosed} />
            )}
          </Flex>
        </Flex>
      </Flex>
    </PageHeader>
  );
});
